'use client';

import React, { useMemo, useRef, useState } from 'react';

type FAQItem = {
	id: string;
	question: string;
	answer: string;
};

type FAQCategory = {
	id: string;
	title: string;
	subtitle: string;
	items: FAQItem[];
	icon: React.ReactNode;
};

const categories: FAQCategory[] = [
	{
		id: 'empresas',
		title: 'Para Empresas',
		subtitle: 'Dúvidas sobre contratação e planos',
		icon: <BuildingIcon />,
		items: [
			{
				id: 'emp-1',
				question: 'O que é a plataforma IncluiPcD?',
				answer:
					'A IncluiPcD conecta empresas comprometidas com diversidade a profissionais com deficiência, com vagas, conteúdos e suporte durante todo o ano.',
			},
			{
				id: 'emp-2',
				question: 'Como posso cadastrar minha empresa e publicar vagas?',
				answer:
					'Você pode criar uma conta da empresa na plataforma e escolher o plano mais adequado. Após isso, já consegue publicar vagas e acompanhar candidaturas.',
			},
			{
				id: 'emp-3',
				question: 'Como acompanhar os candidatos que se inscrevem nas minhas vagas?',
				answer:
					'No painel da empresa você visualiza candidaturas, perfis e o andamento de cada processo seletivo de forma centralizada.',
			},
			{
				id: 'emp-4',
				question: 'A plataforma funciona apenas durante o evento?',
				answer:
					'Não. O evento anual é um marco importante, mas a plataforma permanece ativa o ano inteiro conectando empresas e talentos.',
			},
			{
				id: 'emp-5',
				question: 'Quais são os planos disponíveis para empresas?',
				answer:
					'Os planos variam conforme o nível de divulgação e funcionalidades. Você pode comparar benefícios na área comercial da plataforma.',
			},
			{
				id: 'emp-6',
				question: 'A IncluiPcD oferece suporte para recrutamento inclusivo?',
				answer:
					'Sim. A IncluiPcD apoia empresas com orientações práticas para processos seletivos mais acessíveis e inclusivos.',
			},
		],
	},
	{
		id: 'candidatos',
		title: 'Para Candidatos',
		subtitle: 'Dúvidas sobre vagas e cadastro',
		icon: <UserIcon />,
		items: [
			{
				id: 'cand-1',
				question: 'Como me inscrevo na plataforma?',
				answer:
					'Basta criar sua conta gratuitamente, completar seu perfil e começar a se candidatar às vagas disponíveis.',
			},
			{
				id: 'cand-2',
				question: 'Como encontro vagas adequadas ao meu perfil?',
				answer:
					'Você pode usar filtros por área, localidade e formato de trabalho para encontrar oportunidades aderentes ao seu perfil.',
			},
			{
				id: 'cand-3',
				question: 'Posso me candidatar a vagas de diferentes empresas?',
				answer:
					'Sim. Você pode se candidatar a quantas vagas quiser, em diferentes empresas parceiras.',
			},
			{
				id: 'cand-4',
				question: 'Existe algum custo para candidatos?',
				answer: 'Não. O acesso à plataforma e às vagas é gratuito para candidatos.',
			},
			{
				id: 'cand-5',
				question: 'Como sei se minha candidatura foi vista pela empresa?',
				answer:
					'Você acompanha suas candidaturas pela plataforma e visualiza atualizações sempre que houver movimentação no processo.',
			},
			{
				id: 'cand-6',
				question: 'A plataforma oferece conteúdos de capacitação?',
				answer:
					'Sim. A IncluiPcD disponibiliza conteúdos e painéis com temas de empregabilidade, inclusão e desenvolvimento profissional.',
			},
		],
	},
	{
		id: 'gerais',
		title: 'Dúvidas Gerais',
		subtitle: 'Sobre a plataforma e o evento',
		icon: <CalendarIcon />,
		items: [
			{
				id: 'ger-1',
				question: 'O que é o Evento IncluiPcD?',
				answer:
					'É o evento anual que reúne empresas, especialistas e profissionais para discutir inclusão, acessibilidade e empregabilidade.',
			},
			{
				id: 'ger-2',
				question: 'Como posso participar do evento anual?',
				answer:
					'Você pode acompanhar os canais oficiais e se cadastrar na plataforma para receber as datas e novidades da próxima edição.',
			},
			{
				id: 'ger-3',
				question: 'A plataforma é acessível?',
				answer:
					'Sim. A plataforma é construída com foco em acessibilidade para oferecer uma experiência inclusiva para todas as pessoas.',
			},
			{
				id: 'ger-4',
				question: 'Como entro em contato com o suporte?',
				answer:
					'Você pode usar o contato por e-mail e também os canais oficiais informados na plataforma.',
			},
		],
	},
	{
		id: 'tipos-vaga',
		title: 'Tipos de vaga PCD',
		subtitle: 'Entenda a terminologia das vagas',
		icon: <UserIcon />,
		items: [
			{
				id: 'tipo-1',
				question: 'O que significa "vaga exclusiva para PCD"?',
				answer:
					'É uma vaga reservada exclusivamente para candidatos com deficiência, geralmente publicada por empresas que precisam cumprir a Lei de Cotas (Lei nº 8.213/91). Pessoas sem deficiência não concorrem a essa posição. Para se candidatar, normalmente é necessário declarar e, depois, comprovar a deficiência com laudo médico (CID).',
			},
			{
				id: 'tipo-2',
				question: '"Vaga afirmativa para PCD" é a mesma coisa que "vaga exclusiva"?',
				answer:
					'Na prática, sim — "vaga afirmativa" é um termo usado em ações afirmativas (políticas de inclusão) e costuma funcionar como sinônimo de vaga exclusiva: reservada para grupos sub-representados, no caso, pessoas com deficiência. Vale sempre conferir a descrição da vaga, pois algumas empresas usam o termo de forma mais ampla, incluindo também outros grupos (negros, mulheres, 50+).',
			},
			{
				id: 'tipo-3',
				question: 'O que é uma "vaga elegível para PCD"?',
				answer:
					'É uma vaga aberta ao público geral, mas que também aceita — e às vezes prioriza — candidatos com deficiência compatível com a função. Diferente da vaga exclusiva, aqui você concorre com candidatos sem deficiência também, mas a empresa sinaliza que está apta a receber e adaptar o processo para PCD.',
			},
			{
				id: 'tipo-4',
				question: 'E "vaga inclusiva" ou "vaga extensiva para PCD", o que significam?',
				answer:
					'"Vaga inclusiva" geralmente indica que a empresa tem políticas e estrutura de acessibilidade prontas para receber profissionais com deficiência, mas a vaga não é exclusiva. "Vaga extensiva para PCD" costuma aparecer quando uma vaga originalmente comum é "estendida" — ou seja, aberta também — para candidatos PcD, mantendo os mesmos requisitos da vaga original.',
			},
			{
				id: 'tipo-5',
				question: 'Vi "vaga também para PCD" em um anúncio. Posso me candidatar mesmo sem deficiência?',
				answer:
					'Sim. Quando o anúncio diz "vaga também para PCD", a vaga é aberta ao público geral e a empresa está apenas reforçando que pessoas com deficiência são bem-vindas e terão o processo seletivo adaptado se necessário. Não é uma vaga exclusiva.',
			},
			{
				id: 'tipo-6',
				question: 'No fim das contas, o que é "vaga para PCD" — existe um conceito único?',
				answer:
					'"Vaga para PCD" é um termo guarda-chuva usado para qualquer oportunidade de emprego direcionada, total ou parcialmente, a pessoas com deficiência — seja por exigência da Lei de Cotas, seja por política voluntária de diversidade da empresa. A nomenclatura exata (exclusiva, afirmativa, elegível, inclusiva, extensiva) varia de empresa para empresa, então o ideal é sempre ler a descrição completa da vaga.',
			},
			{
				id: 'tipo-7',
				question: 'Como me cadastro para vagas PCD no Inclui PCD?',
				answer:
					'Acesse incluipcd.com.br, clique em "Cadastre seu currículo" e preencha seu perfil com formação, experiência e tipo de deficiência. Empresas parceiras acessam seu currículo e entram em contato quando há compatibilidade com vagas abertas — exclusivas, afirmativas ou elegíveis.',
			},
			{
				id: 'tipo-8',
				question: 'Tem vagas para PCD em home office?',
				answer:
					'Sim, e a procura por esse formato vem crescendo. Vagas remotas para PCD existem principalmente em TI, atendimento, financeiro e administrativo. No Inclui PCD você pode filtrar oportunidades por modalidade (presencial, híbrido, remoto).',
			},
			{
				id: 'tipo-9',
				question: 'Como encontro vagas para PCD na minha cidade (RJ, BH, Curitiba, SP)?',
				answer:
					'No Inclui PCD, use o filtro de localização ao buscar vagas. As principais regiões metropolitanas concentram a maior parte das oportunidades, mas vagas remotas ampliam o alcance para candidatos de qualquer cidade.',
			},
			{
				id: 'tipo-10',
				question: 'Preciso de laudo médico para comprovar minha deficiência?',
				answer:
					'Sim, na maioria dos processos seletivos para vagas exclusivas/afirmativas a empresa solicitará um laudo médico com CID (Classificação Internacional de Doenças) para validar o enquadramento na cota. Para vagas elegíveis ou inclusivas, o laudo pode ser pedido apenas no momento da admissão. Recomendamos providenciar o documento com antecedência em UBS, AME ou clínica especializada.',
			},
		],
	},
	{
		id: 'lei-cotas',
		title: 'Lei de Cotas',
		subtitle: 'Regras e obrigações para empresas',
		icon: <BuildingIcon />,
		items: [
			{
				id: 'cota-1',
				question: 'A partir de quantos funcionários minha empresa é obrigada a contratar PCD?',
				answer:
					'A partir de 100 funcionários celetistas, conforme a Lei nº 8.213/91 (Lei de Cotas). Empresas com menos de 100 funcionários não têm obrigatoriedade legal — mas isso não impede, e pode até ser estratégico, contratar PCD voluntariamente.',
			},
			{
				id: 'cota-2',
				question: 'Quando exatamente a empresa se torna "obrigada" a contratar PCD?',
				answer:
					'A obrigatoriedade passa a valer assim que a empresa atinge 100 empregados no quadro celetista, e o percentual de cota varia conforme a faixa: 100 a 200 funcionários: 2%; 201 a 500: 3%; 501 a 1.000: 4%; acima de 1.001: 5%. A fiscalização é feita pelo Ministério do Trabalho e Emprego (MTE), que pode notificar a empresa para regularização.',
			},
			{
				id: 'cota-3',
				question: 'Como contratar PCD na prática? Por onde começar?',
				answer:
					'O caminho recomendado é: (1) calcular sua cota com base no número de funcionários, (2) revisar e adaptar processos seletivos e ambiente de trabalho, (3) publicar vagas em plataformas especializadas como o Inclui PCD, que conecta empresas a um banco de currículos segmentado por tipo de deficiência, área e localidade.',
			},
			{
				id: 'cota-4',
				question: 'Contratar PCD dá desconto ou reduz impostos da empresa?',
				answer:
					'O benefício fiscal direto mais comum é via contratação por meio de APAEs e instituições sem fins lucrativos habilitadas, que pode gerar dedução no Imposto de Renda. Além disso, cumprir a cota evita as multas da fiscalização, o que na prática representa economia. Recomendamos consultar um contador para mapear os benefícios aplicáveis ao regime tributário da sua empresa.',
			},
			{
				id: 'cota-5',
				question: 'Qual a multa para empresas que não contratam PCD?',
				answer:
					'A multa por descumprimento da Lei de Cotas é aplicada pelo MTE e varia conforme o porte da empresa e o número de vagas não preenchidas, podendo ir de R$ 2.284 a R$ 231.411,16 por infração. Empresas reincidentes podem sofrer fiscalização mais frequente.',
			},
			{
				id: 'cota-6',
				question: 'Quais empresas são, de fato, obrigadas a contratar PCD?',
				answer:
					'Toda empresa privada com 100 ou mais funcionários celetistas, independentemente do setor, está sujeita à Lei de Cotas. Órgãos públicos seguem regras próprias (Lei nº 8.112/90), com reserva de até 20% das vagas em concursos.',
			},
			{
				id: 'cota-7',
				question: 'Minha empresa tem menos de 100 funcionários. Vale a pena contratar PCD mesmo sem obrigação?',
				answer:
					'Sim. Empresas pequenas que se antecipam constroem cultura inclusiva desde cedo, evitam adaptações emergenciais quando crescerem além de 100 funcionários, e fortalecem employer branding — cada vez mais relevante para atrair talentos e clientes que valorizam diversidade.',
			},
			{
				id: 'cota-8',
				question: 'Quais os benefícios de contratar PCD além de cumprir a lei?',
				answer:
					'Empresas relatam ganhos em retenção de talentos, redução de turnover, fortalecimento da marca empregadora (ESG/D&I) e ampliação da diversidade de perspectivas em times. Também é um diferencial competitivo em licitações públicas e parcerias que avaliam critérios de diversidade.',
			},
			{
				id: 'cota-9',
				question: 'Como adaptar o ambiente de trabalho para receber um colaborador PCD?',
				answer:
					'Depende do tipo de deficiência: acessibilidade física (rampas, banheiros adaptados, sinalização tátil), tecnológica (leitores de tela, softwares de acessibilidade, teclados adaptados) e cultural (treinamento de líderes e equipes, ajuste de processos seletivos). O Inclui PCD orienta empresas nesse diagnóstico inicial.',
			},
			{
				id: 'cota-10',
				question: 'Como o Inclui PCD pode ajudar minha empresa a contratar PCD de forma estruturada?',
				answer:
					'O Inclui PCD é uma plataforma SaaS que conecta empresas a candidatos PcD qualificados, com banco de currículos segmentado, publicação de vagas e acompanhamento do processo seletivo. Planos a partir de R$ 999/mês (Bronze), com o plano Gold incluindo página personalizada, integração com ATS e divulgações nas redes sociais do Inclui PCD.',
			},
		],
	},
];

function SearchIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
			<circle cx="11" cy="11" r="7" stroke="#9CA3AF" strokeWidth="2" />
			<path d="M20 20L16.65 16.65" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function ChevronDownIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function BuildingIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
			<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" stroke="white" strokeWidth="2" />
			<path d="M6 12H4a2 2 0 0 0-2 2v8h4" stroke="white" strokeWidth="2" />
			<path d="M18 9h2a2 2 0 0 1 2 2v11h-4" stroke="white" strokeWidth="2" />
			<path d="M10 6h4M10 10h4M10 14h4" stroke="white" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function UserIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
			<circle cx="12" cy="8" r="4" stroke="white" strokeWidth="2" />
			<path d="M5 21a7 7 0 0 1 14 0" stroke="white" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function CalendarIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
			<rect x="3" y="5" width="18" height="16" rx="3" stroke="white" strokeWidth="2" />
			<path d="M16 3v4M8 3v4M3 10h18" stroke="white" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function ArrowRightIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M5 12h14" />
			<path d="m12 5 7 7-7 7" />
		</svg>
	);
}

function StillHaveQuestionsCard() {
	return (
		<div className="mt-24 text-center max-md:mt-16">
			<div className="inline-block rounded-[24px] border-[1px] border-solid border-brand-light bg-brand-main/5 p-10 text-center max-md:p-8">
				<h2 className="mb-3 text-[24px] font-bold leading-tight tracking-[-0.01em] text-[#111827]">
					Ainda tem dúvidas?
				</h2>
				<p className="mx-auto mb-6 max-w-[430px] text-[16px] leading-[1.55] text-[#4b5563]">
					Nossa equipe está pronta para ajudar você. Entre em contato e responderemos em até 48 horas.
				</p>

				<a
					href="mailto:contato@incluipcd.com.br?subject=Dúvida%20-%20FAQ%20IncluiPcD"
					className="group inline-flex items-center gap-2 rounded-full bg-brand-main px-8 py-4 text-[16px] font-semibold text-white shadow-[0_10px_15px_-3px_rgba(15,25,215,0.3)] transition-all duration-300 hover:bg-brand-dark hover:shadow-[0_10px_15px_-3px_rgba(15,25,215,0.5)]"
				>
					Fale Conosco
					<span className="transition-transform duration-300 group-hover:translate-x-1">
						<ArrowRightIcon />
					</span>
				</a>
			</div>
		</div>
	);
}

function FAQAccordionItem({
	item,
	isOpen,
	onToggle,
}: {
	item: FAQItem;
	isOpen: boolean;
	onToggle: () => void;
}) {
	const contentRef = useRef<HTMLDivElement>(null);
	const contentHeight = isOpen ? `${contentRef.current?.scrollHeight ?? 0}px` : '0px';
	const contentId = `faq-content-${item.id}`;
	const triggerId = `faq-trigger-${item.id}`;

	return (
		<div className="overflow-hidden rounded-[14px] border border-[#eef2f7] bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-[1px] hover:shadow-[0_12px_26px_rgba(15,25,215,0.12)]">
			<button
				id={triggerId}
				type="button"
				aria-expanded={isOpen}
				aria-controls={contentId}
				onClick={onToggle}
				className="group flex w-full items-center justify-between gap-6 px-5 py-[18px] text-left outline-none [-webkit-tap-highlight-color:transparent] active:bg-white"
			>
				<span
					className={`text-[15px] font-bold leading-[1.35] transition-colors duration-300 ${
						isOpen ? 'text-brand-main' : 'text-[#0f172a] group-hover:text-brand-main'
					}`}
				>
					{item.question}
				</span>
				<span
					className={`shrink-0 transition-all duration-300 ${
						isOpen ? 'rotate-180 text-brand-main' : 'text-[#334155] group-hover:text-brand-main'
					}`}
				>
					<ChevronDownIcon />
				</span>
			</button>

			<div
				id={contentId}
				ref={contentRef}
				role="region"
				aria-labelledby={triggerId}
				style={{ maxHeight: contentHeight }}
				className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
					isOpen ? 'opacity-100' : 'opacity-0'
				}`}
			>
				<div className="px-5 pb-5">
					<p className="border-t border-[#f1f5f9] pt-4 text-[14px] leading-[1.55] text-[#475569]">{item.answer}</p>
				</div>
			</div>
		</div>
	);
}

function FAQGroup({
	category,
	filter,
	fullWidth = false,
}: {
	category: FAQCategory;
	filter: string;
	fullWidth?: boolean;
}) {
	const filteredItems = useMemo(() => {
		if (!filter.trim()) {
			return category.items;
		}

		const normalizedFilter = filter.toLowerCase();
		return category.items.filter(
			(item) =>
				item.question.toLowerCase().includes(normalizedFilter) ||
				item.answer.toLowerCase().includes(normalizedFilter),
		);
	}, [category.items, filter]);
	const [openItemId, setOpenItemId] = useState<string | null>(null);

	return (
		<div className={fullWidth ? 'mx-auto w-full max-w-[860px]' : 'w-full'}>
			<div className="mb-7 flex items-center gap-4">
				<div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[14px] bg-brand-main shadow-[0_12px_26px_rgba(15,25,215,0.3)]">
					{category.icon}
				</div>
				<div>
					<h2 className="text-[24px] font-extrabold leading-[1.12] tracking-[-0.02em] text-[#0f172a] max-md:text-[22px]">
						{category.title}
					</h2>
					<p className="mt-[3px] text-[15px] font-normal leading-[1.35] text-[#6b7280]">{category.subtitle}</p>
				</div>
			</div>

			{filteredItems.length === 0 ? (
				<div className="rounded-[14px] border border-[#e5e7eb] bg-white px-6 py-5 text-[15px] text-[#64748b] shadow-sm">
					Nenhuma pergunta encontrada para este termo.
				</div>
			) : (
				<div className="space-y-4">
					{filteredItems.map((item) => (
						<FAQAccordionItem
							key={item.id}
							item={item}
							isOpen={openItemId === item.id}
							onToggle={() => setOpenItemId((currentId) => (currentId === item.id ? null : item.id))}
						/>
					))}
				</div>
			)}
		</div>
	);
}

export default function FAQQuestionsSection() {
	const [search, setSearch] = useState('');

	return (
		<section className="bg-white px-6 py-20 max-md:px-5 max-md:py-14">
			<div className="mx-auto max-w-[1160px]">
				<div className="mx-auto mb-14 w-full max-w-[720px]">
					<label
						htmlFor="faq-search"
						className="flex h-[56px] items-center gap-3 rounded-full border border-[#e5e7eb] bg-white px-5 shadow-[0_5px_18px_rgba(15,23,42,0.06)]"
					>
						<SearchIcon />
						<input
							id="faq-search"
							type="text"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Buscar nas perguntas frequentes..."
							className="h-full w-full bg-transparent text-[15px] font-medium text-[#0f172a] placeholder:text-[#9ca3af] outline-none"
						/>
					</label>
				</div>

				<div className="grid grid-cols-1 gap-x-14 gap-y-12 md:grid-cols-2">
					<FAQGroup category={categories[0]} filter={search} />
					<FAQGroup category={categories[1]} filter={search} />
				</div>

				<div className="mt-16">
					<FAQGroup category={categories[2]} filter={search} fullWidth />
				</div>

				<div className="mt-16">
					<FAQGroup category={categories[3]} filter={search} fullWidth />
				</div>

				<div className="mt-16">
					<FAQGroup category={categories[4]} filter={search} fullWidth />
				</div>

				<StillHaveQuestionsCard />
			</div>
		</section>
	);
}
